import { Ref, ReactElement, RefAttributes, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  GroupBase,
  Select,
  SelectInstance,
  SelectProps,
  useMultiSelect,
} from '@any-ui-react/select';

import { DdiProductSegment } from '@anyx/common-graphql';
import { BasicOption, ObjectUtils } from '@anyx/shared-utils';

type SegmentationOptions = BasicOption<string>;

interface SegmentationSelectProps<IsMulti extends boolean = boolean> extends SelectProps<
  SegmentationOptions,
  IsMulti
> {
  isMulti?: IsMulti;
}

const SegmentationSelectGn = (
  { isMulti = true, ...rest }: SegmentationSelectProps,
  ref:
    | Ref<
        SelectInstance<
          { label: string; value: string },
          boolean,
          GroupBase<{ label: string; value: string }>
        >
      >
    | undefined
) => {
  const { t } = useTranslation(['report', 'marketplace', 'shared']);
  const options = ObjectUtils.arrayFromEnum(DdiProductSegment).map((option) => ({
    label: t('report.page.inventoryTracker.segment', { ns: 'report', segment: option }),
    value: option,
  }));

  const getSelectedLabel = (selectedCount: number) => {
    if (selectedCount === options.length) {
      return t('shared.action.all', {
        ns: 'shared',
        entity: t('marketplace.page.product.bulk.filter.placeholder.segmentation', {
          ns: 'marketplace',
        }),
      });
    }
    return t('shared.select.optionSelected', { ns: 'shared', amount: selectedCount });
  };

  const selectProps = useMultiSelect({
    options,
    getSelectedLabel,
  });

  return (
    <Select
      isClearable
      isSearchable
      placeholder={t('marketplace.page.product.bulk.filter.placeholder.segmentation', {
        ns: 'marketplace',
      })}
      {...rest}
      {...selectProps}
      isMulti={isMulti}
      // TODO FIX THIS
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      ref={ref}
    />
  );
};

type SegmentationSelect<IsMulti extends boolean = boolean> = (
  props: SegmentationSelectProps &
    RefAttributes<SelectInstance<SegmentationOptions, IsMulti, GroupBase<SegmentationOptions>>>
) => ReactElement;

export const SegmentationSelect = forwardRef(SegmentationSelectGn) as <
  IsMulti extends boolean = boolean,
>(
  p: SegmentationSelectProps<IsMulti> & {
    ref?:
      | Ref<
          SelectInstance<
            { label: string; value: string },
            boolean,
            GroupBase<{ label: string; value: string }>
          >
        >
      | undefined;
  }
) => ReactElement;
